class MenuAim extends HTMLElement {
  constructor() {
    super();
    this.activeIndex = -1;
    this.pendingIndex = -1;
    this.menuItems = [];
    this.deactivateTimeout = null;
    this.pendingDeactivateIndex = -1;
    // Bind the event handlers to the instance
    this.handlePositionUpdate = this.onPositionUpdate.bind(this);
    this.handleMouseEnter = this.onMouseEnter.bind(this);
    this.handleMouseLeave = this.onMouseLeave.bind(this);

    this.startTracking = () => {};
  }

  connectedCallback() {
    this.init();
    this.dispatchEvent(new CustomEvent('menu-aim:connected'));
  }

  disconnectedCallback() {
    this.destroy();
  }

  /**
   * Initialize the menu aim.
   * - Find all menu items
   * - Set up the startTracking function by calling the trackMouseMovement function
   * - Add mouse enter and leave event listeners to each menu item
   */
  init() {
    this.menuItems = Array.from(this.querySelectorAll('[data-menu-aim="item"]'));
    if (!this.menuItems.length) {
      return;
    }

    this.startTracking = this.trackMouseMovement(this.handlePositionUpdate);

    this.menuItems.forEach((item) => {
      item.addEventListener('mouseenter', this.handleMouseEnter, { passive: true });
      item.addEventListener('mouseleave', this.handleMouseLeave, { passive: true });
    });

    /**
     * Check if any of the menu items are being hovered on component mount.
     * - If a menu item is being hovered, activate it.
     */
    const hoveredItemIndex = this.menuItems.findIndex((item) => item.matches(':hover'));
    if (hoveredItemIndex > -1) {
      this.activateItem(hoveredItemIndex);
    }
  }

  /**
   * Remove all event listeners from the menu items.
   */
  destroy() {
    this.menuItems.forEach((item) => {
      item.removeEventListener('mouseenter', this.handleMouseEnter);
      item.removeEventListener('mouseleave', this.handleMouseLeave);
    });
  }

  /**
   * Handle the position update.
   * - Checks the distance that the mouse has moved
   * - If the distance moved is less than 60px, the user has likely lingered on an
   *   item, so we will deactivate the currently active item and activate the
   *   pending item, we will also stop tracking the mouse movement by calling the
   *   stop function
   * @param {Object} positionData - position data from the trackMouseMovement callback
   * @param {Number} positionData.distance - distance the mouse has moved
   * @param {Function} positionData.stop - stop function to stop tracking the
   *  mouse movement
   * @param {Function} positionData.position - current position of the mouse
   */
  onPositionUpdate({ distance, stop }) {
    if (distance > 60) {
      return;
    }

    /**
     * There's an active item, so we need to deactivate it.
     */
    if (this.activeIndex > -1) {
      this.deactivateItemWithClosingDelay(this.activeIndex);
    }

    /**
     * There's a pending item to activate.
     */
    if (this.pendingIndex > -1) {
      /**
       * If there's a pending deactivation (a deactivation timeout is set because
       * the user hovered off all items), we should clear that timeout and
       * immediately deactivate the item that was pending deactivation before
       * activating the new item.
       */
      if (this.pendingDeactivateIndex > -1) {
        clearTimeout(this.deactivateTimeout);
        this.deactivateItem(this.pendingDeactivateIndex);
      }

      this.activateItem(this.pendingIndex);
    }

    stop();
  }

  /**
   * Handle mouse enter event.
   * - Set the pending index to the index of the menu item that was entered
   * - Start tracking the mouse movement
   * @param {MouseEvent} event mouse enter event
   */
  onMouseEnter(event) {
    this.pendingIndex = this.menuItems.indexOf(event.currentTarget);
    this.startTracking();
  }

  /**
   * Handle mouse leave event.
   * - Reset the pending index
   * - Start tracking the mouse movement
   */
  onMouseLeave() {
    this.pendingIndex = -1;
    this.startTracking();
  }

  /**
   * Active a menu item.
   * - Dispatch a custom event to notify that the menu item has been activated
   *   - Payload contains the item and index of the activated item
   * - Store the index of the active item
   * @param {Number} index index of the menu item to activate
   */
  activateItem(index) {
    const item = this.menuItems[index];
    const customEvent = new CustomEvent('menu-aim:activate', {
      detail: { item, index },
    });
    this.dispatchEvent(customEvent);
    this.activeIndex = index;
  }

  /**
   * Deactivate a menu item.
   * - Dispatch a custom event to notify that the menu item has been deactivated
   *   - Payload contains the item and index of the deactivated item
   * - Reset the active index
   * @param {Number} index index of the menu item to deactivate
   */
  deactivateItem(index) {
    const item = this.menuItems[index];
    const customEvent = new CustomEvent('menu-aim:deactivate', {
      detail: { item, index },
    });
    this.dispatchEvent(customEvent);
    this.activeIndex = -1;
  }

  /**
   * Deactivate the menu item, with an optional closing delay if there's no
   * pending index to switch to (i.e. the user has hovered off all items).
   * - If there's a pending index, we will immediately deactivate the item
   * - If there's no pending index, we will set a timeout to deactivate the item
   *   after the specified duration.
   * - The timeout is checked and cleared in `onPositionUpdate` if the user
   *   hovers over another item before the timeout completes.
   * @param {Number} index
   */
  deactivateItemWithClosingDelay(index) {
    /**
     * Duration to wait before deactivating the item if there's no pending index.
     * - This gives a longer delay if the user hovers off the menu item
     *   completely.
     * - This duration can be tweaked to suit the desired user experience.
     */
    const delayDuration = 750;

    if (this.pendingIndex === -1) {
      clearTimeout(this.deactivateTimeout);
      this.pendingDeactivateIndex = index;
      this.deactivateTimeout = setTimeout(() => {
        this.deactivateItem(index);
        this.pendingDeactivateIndex = -1;
        this.deactivateTimeout = null;
      }, delayDuration);
      return;
    }

    this.deactivateItem(index);
  }

  /**
   * Initialize mouse movement tracking.
   * - Contains functions for starting and stopping tracking.
   * - Starting the tracking will add a mousemove event listener to the document
   *   and start a timer to check the mouse position every interval.
   * - Stopping the tracking will remove the mousemove event listener and clear
   *   the timer.
   * - The callback function will be called with the position data and a stop
   *   function that can be called to stop tracking the mouse movement.
   * @param {Function} callback
   * @param {Object} options
   * @param {Number} options.interval - interval to check the mouse position.
   * @returns {Function} startTracking - function to start tracking the mouse movement.
   */
  trackMouseMovement(callback, { interval = 150 } = {}) {
    let timerId = 0;
    let previousPosition = null;
    let currentPosition = null;

    /**
     * Handle the mouse move.
     * - Sets the previous position if it is not set
     * - Updates the current position of the mouse
     * @param {MouseEvent} event mouse move event
     */
    function onMouseMove(event) {
      const position = { x: event.clientX, y: event.clientY };
      if (!previousPosition) {
        previousPosition = position;
      }
      currentPosition = position;
    }

    /**
     * Calculate the distance and position of the mouse.
     * - Calculates the distance moved by the mouse using the previous and current
     *   positions
     * - Calls the callback function with the distance, current position and the
     *   `stopTracking` function so that the tracking can be programmatically stopped
     * - Updates the previous position to the current position
     */
    function checkPosition() {
      /**
       * If there's no position data, we can't calculate the distance, so we'll
       * return early.
       */
      if (!previousPosition || !currentPosition) {
        return;
      }

      const distance = Math.hypot(previousPosition.x - currentPosition.x, previousPosition.y - currentPosition.y);

      callback({
        distance,
        position: currentPosition,
        stop: stopTracking,
      });

      previousPosition = { x: currentPosition.x, y: currentPosition.y };
    }

    /**
     * Start tracking the mouse movement.
     * - If the timer is not already running, we will:
     *   - Add a mousemove event listener to the document
     *   - Start a timer to check the mouse position every interval
     */
    function startTracking() {
      if (timerId) {
        return;
      }

      document.addEventListener('mousemove', onMouseMove, { passive: true });
      timerId = setInterval(checkPosition, interval);
    }

    /**
     * Stop tracking the mouse movement.
     * - Remove the mousemove event listener from the document
     * - Clear the timer
     */
    function stopTracking() {
      document.removeEventListener('mousemove', onMouseMove);
      clearInterval(timerId);
      timerId = 0;
    }

    return startTracking;
  }
}

customElements.define('menu-aim', MenuAim);
